import { Category } from '@wix/answers-api/dist/kb';
import { Box, Pagination } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { FunctionComponent } from 'react';
import { animateScroll } from 'react-scroll';

import { DATA_HOOKS } from '../../dataHooks';
import { SEARCH_RESULTS_PAGE_SIZE } from '../../pages/search';
import { ArticleSearchResult } from '../../types';
import { Breadcrumbs } from '../Breadcrumbs';
import { SkeletonLoading } from './SkeletonLoading';
import { ContactSupport } from '../ContactSupport';
import { SearchResultsHeader } from './SearchResultsHeader';
import { SearchResultItems } from './SearchResultsItems';
import { useIsGenAnswer } from '../../hooks/useIsGenAnswer';
const GeneratedAnswer = dynamic(() => import('./GeneratedAnswer'), {
  ssr: false,
});

import css from './index.module.scss';

export type SearchResultsProps = {
  term: string;
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  generatedAnswer: string | null;
  usedArticlesInfo: { [id: string]: string };
  isProhibited: boolean | null;
  showContactSupport?: boolean;
  retryGenerateAnswer: () => Promise<void>;
  isStreaming: boolean;
};

export const SearchResults: FunctionComponent<SearchResultsProps> = ({
  term,
  searchResults,
  categoriesPaths,
  currentPage,
  onPageChange,
  isLoading,
  generatedAnswer,
  usedArticlesInfo,
  isProhibited,
  showContactSupport,
  retryGenerateAnswer,
  isStreaming,
}) => {
  const { t } = useTranslation();
  const { itemsCount, searchMethod } = searchResults;

  const isNextGenSearchEnabled = useIsGenAnswer();
  const displayGeneratedAnswer =
    isNextGenSearchEnabled && currentPage === 1 && !showContactSupport;
  const displayGeneratedAnswerLoading =
    isNextGenSearchEnabled &&
    generatedAnswer === null &&
    currentPage === 1 &&
    !showContactSupport;

  const onPageChangeEvent = async (page: number) => {
    animateScroll.scrollToTop();
    onPageChange(page);
  };

  const isAnyLoading = isLoading || displayGeneratedAnswerLoading;
  const shouldShowPagination =
    !isAnyLoading && itemsCount > SEARCH_RESULTS_PAGE_SIZE;

  const numPages = Math.ceil(itemsCount / SEARCH_RESULTS_PAGE_SIZE);

  const breadcrumbItems = [
    { id: 'search-results', label: t('search-results-page.breadcrumb') },
  ];

  return (
    <Box
      direction="vertical"
      className={css.searchResultsWrapper}
      dataHook={DATA_HOOKS.SEARCH_RESULTS}
    >
      <Breadcrumbs breadcrumbItems={breadcrumbItems} shallow={false} />
      {showContactSupport && (
        <ContactSupport searchMethod={searchMethod as string} />
      )}
      {displayGeneratedAnswer && (
        <Box className={css.searchResultGenAnswer}>
          <GeneratedAnswer
            searchQuestion={term}
            searchAnswer={generatedAnswer}
            usedArticlesInfo={usedArticlesInfo}
            isProhibited={isProhibited}
            retryGenerateAnswer={retryGenerateAnswer}
            isStreaming={isStreaming}
          />
        </Box>
      )}
      <Box
        direction="vertical"
        className={classNames(css.searchResults, {
          [css.includeGeneratedAnswer]: displayGeneratedAnswer,
        })}
      >
        {!isLoading && (
          <SearchResultsHeader
            term={term}
            searchResults={searchResults}
            displayGeneratedAnswer={displayGeneratedAnswer}
            displayGeneratedAnswerLoading={displayGeneratedAnswerLoading}
          />
        )}
        {isLoading ? (
          <SkeletonLoading />
        ) : (
          <SearchResultItems
            searchResults={searchResults}
            categoriesPaths={categoriesPaths}
            term={term}
          />
        )}
      </Box>
      {shouldShowPagination && (
        <Pagination
          className={css.pagination}
          onChange={(e) => onPageChangeEvent(e.page)}
          currentPage={currentPage}
          totalPages={numPages}
          dataHook={DATA_HOOKS.SEARCH_RESULTS_PAGINATION}
        />
      )}
    </Box>
  );
};
